import _ from "lodash";
import { useEffect, useState } from "react";
import {
  PageContainer,
  PageContent,
  PageFooterBar,
} from "../../components/PageContainer";
import apiAgent from "../../api/apiAgent";
import { useNavigate, useParams } from "react-router";
import { BreadCrumbs, TitleBar } from "../../components/TitleBar";
import { useAtomValue } from "jotai";
import { modelDataAtom, useValidateModel } from "../../atoms";
import { BackButton, DeleteButton, EditButton } from "../../components/Button";
import { Link } from "react-router-dom";
import { getDataAndSchemaFromModel } from "../../components/Forms/utils";
import { DisplayValue } from "../../components/DisplayValue";
import { RelatedItemLists } from "../../components/Lists/ItemLists";
import { tMsg } from "../../components/ToastNotifications";
import { checkTitle, parseModelToReadable, useOriginParams } from "../../utils";
import { useSetChangesMade } from "../../components/Publishing/utils";
import { DeletePopup } from "../../components/ConfirmationPopup";
import { handleSchema } from "./customisation/MediaItemForm";

const ShowMediaItem = ({
  modelName,
}: {
  modelName: "media_item" | "attract_media_item";
}): JSX.Element => {
  const params = useParams();
  const [originFrom, originId] = useOriginParams();
  // const originURLParams = `?from=${originFrom}&id=${originId}`;
  // const modelName = "media_item";
  useValidateModel(modelName);
  const setChanges = useSetChangesMade(modelName);

  const modelData = useAtomValue(modelDataAtom);
  const [schema, setSchema] = useState<any[]>([]);
  const [relationships, setRelationships] = useState<any[]>([]);
  const [isLoadingSchema, setIsLoadingSchema] = useState<boolean>(true);
  // const [relatedItems, setRelatedItems] = useState<any[][]>([]);
  const [itemData, setItemData] = useState<any>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [showDeletePopup, setShowDeletePopup] = useState<boolean>(false);

  const navigate = useNavigate();

  useEffect(() => {
    fetchData();
  }, [modelData, modelName]);

  const fetchData = async () => {
    try {
      const response = await apiAgent.DataType.get(modelName, params?.id || -1);
      const item = response.data[0] || null;
      setItemData(item);

      if (modelData && modelData !== null && isLoadingSchema) {
        const [, getSchema, getRelationships] = getDataAndSchemaFromModel(
          modelData,
          modelName,
          true
        );

        setSchema(
          handleSchema(item.type, `${originFrom}_media` || "", getSchema)
        );
        setRelationships(getRelationships);
        setIsLoadingSchema(false);
      }
    } catch (err) {
      console.log(err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeletePressed = async () => {
    try {
      if (!params.id) throw "No ID specified.";
      const response = await apiAgent.DataType.delete({
        model_name: modelName,
        target_ids: [_.toNumber(params.id)],
      });
      if (response.status === 200) {
        tMsg.success(`Item deleted`);
        setChanges();
        if (originFrom && originId)
          navigate(`/m/${originFrom}/show/${originId}`);
        else navigate(`/m/${modelName}`);
      }
    } catch (err) {
      // TODO: add any error in response to notifications here
      tMsg.error(`Error deleting ${modelName}`);
      console.log(err);
    }
  };

  const crumbs =
    originFrom && originId
      ? [
          {
            label: `${checkTitle(originFrom)} - ${originId}`,
            link: `/m/${originFrom}/${originId}`,
          },
        ]
      : [
          {
            label: `${checkTitle(modelName)} - ${itemData.id}`,
            link: `/m/${modelName}/${itemData.id}`,
          },
        ];

  const customModel = `${originFrom}_media`;

  return (
    <PageContainer
      isLoading={isLoading || isLoadingSchema}
      className="relative"
    >
      <PageContent reduceHeightForFooter>
        {/* <BreadCrumbs crumbs={crumbs} /> */}
        {
          // TODO: Create a better display if id/itemData is invalid
          params.id && itemData !== null ? (
            <>
              <BreadCrumbs crumbs={crumbs} />
              <TitleBar title={checkTitle(modelName)}>
                <BackButton />
              </TitleBar>
              <div className="flex flex-col gap-4">
                {schema.map((item: any, index: number) => (
                  <DisplayValue
                    key={index}
                    name={item.name}
                    model={modelName}
                    value={itemData[item.name]}
                    type={item.type}
                  />
                ))}
              </div>
              {
                // Show any items related to this one and
                // give the option to create a new related item
                relationships && relationships.length > 0 && (
                  <RelatedItemLists
                    relationships={relationships}
                    model={modelName}
                    id={parseInt(params.id)}
                  />
                )
              }
              {itemData.thumbUrl && (
                <img src={itemData.thumbnail_src} className="max-w-full py-4" />
              )}
              {itemData.fullImageUrl && (
                <img
                  src={itemData.full_resolution_image}
                  className="max-w-full py-4"
                />
              )}
            </>
          ) : (
            <TitleBar title={`Item ${params.id} not found`} />
          )
        }
      </PageContent>
      <PageFooterBar>
        {params.id && itemData !== null && (
          <>
            <Link
              to={`/m/${modelName}/edit/${params.id}?from=${originFrom}&id=${originId}`}
            >
              <EditButton />
            </Link>
            <DeleteButton onClick={() => setShowDeletePopup(true)} />
            <DeletePopup
              showModal={showDeletePopup}
              setShowModal={setShowDeletePopup}
              confirmAction={() => {
                handleDeletePressed();
                setShowDeletePopup(false);
              }}
            />
          </>
        )}
      </PageFooterBar>
    </PageContainer>
  );
};

export default ShowMediaItem;
